import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  dateFormatter,
  decodeQueryData,
  encoder,
  getSortType,
  logger,
  modalNotification,
  navigateWithParam,
  readMoreTextShow,
} from "utils";
import { ManageBiographyServices, ThemeService } from "services";
import { dateWithString, nav } from "helpers";
import {
  actionFormatter,
  Breadcrumb,
  checkValidData,
  DataTable,
  ListingHeader,
  ManageReportedFilter,
  middleNameFormatter,
  ModalComponent,
  PageHeader,
  serialNumberFormatter,
  switchFormatter,
  textFormatter,
} from "../../../../components";

function ReportBiographies() {
  const [readData, setReadData] = useState();
  const [showReadMore, setShowReadMore] = useState(false);
  const [reportedBiographyList, setReportedBiographyList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [noOfPage, setNoOfPage] = useState();
  const [sizePerPage, setSizePerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [param, setParam] = useState({});
  const [searchName, setSearchName] = useState();
  const [visible, setVisible] = useState(false);
  const [page, setPage] = useState(1);
  const [filterData, setFilterData] = useState({});
  const [statusLoading, setStatusLoading] = useState(false);
  const [reportCategory, setReportCategory] = useState([]);
  const [reportCategoryLoader, setReportCategoryLoader] = useState(false);
  const { t } = useTranslation();

  const navigate = useNavigate();

  const location = useLocation();

  const { pathname, search } = location;
  const [defaultSort, setDefaultSort] = useState([
    {
      dataField: "",
      order: "",
    },
  ]);

  const getReportedBiographies = async () => {
    setLoading(true);
    try {
      let queryParams = {
        offset: (page - 1) * sizePerPage,
        limit: sizePerPage,
        sortBy: param?.sortBy,
        sortType: param?.sortType,
        search: searchName,
        ...filterData,
      };
      const res = await ManageBiographyServices.reportedBioGraphyServices(
        queryParams
      );
      const { data, success } = res;
      if (data && success) {
        setReportedBiographyList(data?.rows);
        setNoOfPage(data?.count > 0 ? Math.ceil(data?.count / sizePerPage) : 1);
        setTotalCount(data?.count);
      }
    } catch (err) {
      logger(err);
    }
    setLoading(false);
  };

  const onChangeStatus = async (val, row) => {
    setStatusLoading(true);
    try {
      let bodyData = {
        isActive: val === true ? 1 : 0,
      };
      const res = await ManageBiographyServices.updateBioGraphyStatusServices(
        row?.memorial?.id,
        bodyData
      );
      const { message, success } = res;
      if (success) {
        modalNotification({
          type: "success",
          message,
        });
        setReportedBiographyList([]);
        getReportedBiographies();
      } else {
        modalNotification({
          type: "error",
          message,
        });
      }
    } catch (error) {
      logger(error);
    }
    setStatusLoading(false);
  };

  const getReportCategory = async () => {
    setReportCategoryLoader(true);
    try {
      const res = await ThemeService.GetReportCategoryService();
      const { success, data } = res;
      if (data && success) {
        setReportCategory(data.rows);
      }
    } catch (err) {
      logger(err);
    }
    setReportCategoryLoader(false);
  };

  useEffect(() => {
    if (search) {
      const data = decodeQueryData(search);
      setParam(data);
      setPage(data?.page ?? 1);
      // setSearchName(data?.name ?? "");
      if (data?.sortType) {
        const sortData = [
          {
            order: getSortType(data?.sortType),
            dataField: data?.sortBy,
          },
        ];
        setDefaultSort(sortData);
      } else {
        setDefaultSort({
          dataField: "",
          order: "",
        });
      }
    }
  }, [location]);

  const tableReset = () => {
    setLoading(true);
    setReportedBiographyList([]);
    setNoOfPage(0);
    setTotalCount(0);
  };

  useEffect(() => {
    if (search && JSON.stringify(param) !== "{}") {
      getReportedBiographies();
    }
  }, [param]);

  const getSearchValue = (val) => {
    setSearchName(val);
    if (val) {
      tableReset();
    }
  };

  useEffect(() => {
    if (!search) {
      getReportedBiographies();
    }
    getReportCategory();
  }, []);

  const onReset = () => {
    setFilterData({});
    tableReset();
    setVisible(false);
    const newParams = { ...param };
    newParams.page = 1;
    navigateWithParam(newParams, navigate, pathname);
  };

  const onSortColumn = (field, order) => {
    const data = { ...param };
    data.sortBy = field;
    data.sortType = order === "asc" ? "ASC" : "DESC";
    navigateWithParam(data, navigate, pathname);
    tableReset();
  };

  const headerSortingClasses = (column, sortOrder) => {
    return sortOrder === "asc" ? "sorting_asc" : "sorting_desc";
  };

  const handleFilterSubmit = async (val) => {
    setLoading(true);
    try {
      setFilterData(val);
      tableReset();
      const newParams = { ...param };
      newParams.page = 1;
      navigateWithParam(newParams, navigate, pathname);
      setVisible(false);
      document.body.click();
    } catch (error) {
      logger(error);
    }
    setLoading(false);
  };

  const showMoreText = (data) => {
    setShowReadMore(true);
    setReadData(data?.data);
  };
  const onCloseDescriptionModal = () => {
    setShowReadMore(false);
    setReadData("");
  };

  const options = (row) => {
    const optionsArr = [
      {
        name: "View",
        icon: "icon ni ni-eye",
        action: "redirect",
        path: `${nav[row?.memorial?.themeOnes?.[0]?.type]}/${encoder(
          row?.memorial?.id
        )}?${encoder(row?.memorial?.themeOnes?.[0]?.type)}`,
        target: "blank",
      },
    ];
    return optionsArr;
  };
  const breadcrumb = [
    {
      path: "/admin/dashboard",
      name: "DASHBOARD",
    },
    {
      path: "#",
      name: "Manage Biographies",
    },
  ];

  const columns = [
    {
      dataField: "id",
      text: "S.No.",
      headerClasses: "w_70",
      headerSortingClasses,
      formatter: (cell, row, index) =>
        serialNumberFormatter(page, sizePerPage, index),
    },
    {
      dataField: "creatorName",
      text: "Creator Name",
      headerClasses: "sorting",
      headerSortingClasses,
      sort: true,
      onSort: (field, order) => {
        onSortColumn(field, order);
      },
      formatter: (cell, row) =>
        textFormatter(
          middleNameFormatter(
            textFormatter(row?.memorial?.user?.firstName),
            textFormatter(row?.memorial?.user?.middleName),
            textFormatter(row?.memorial?.user?.lastName)
          )
        ),
    },
    {
      dataField: "email",
      text: "Email Address",
      headerClasses: "sorting",
      sort: true,
      headerSortingClasses,
      onSort: (field, order) => {
        onSortColumn(field, order);
      },
      formatter: (cell, row) => textFormatter(row?.memorial?.user?.email),
    },
    {
      dataField: "phoneNumber",
      text: "Phone Number",
      headerClasses: "sorting",
      sort: true,
      onSort: (field, order) => {
        onSortColumn(field, order);
      },
      headerSortingClasses,
      formatter: (cell, row) => textFormatter(row?.memorial?.user?.phoneNumber),
    },

    {
      dataField: "address",
      text: "Address",
      headerClasses: "sorting",
      sort: true,
      onSort: (field, order) => {
        onSortColumn(field, order);
      },
      headerSortingClasses,
      formatter: (cell, row) => (
        <> {textFormatter(checkValidData(row?.memorial?.location))}</>
      ),
    },
    {
      dataField: "createdAt",
      text: "Created On",
      headerClasses: "sorting",
      headerSortingClasses,
      sort: true,
      onSort: (field, order) => {
        onSortColumn(field, order);
      },
      formatter: (cell, row) =>
        dateFormatter(row?.memorial?.createdAt, dateWithString),
    },
    {
      dataField: "reportedBy",
      text: "Reported By",
      headerClasses: "sorting",
      headerSortingClasses,
      sort: true,
      onSort: (field, order) => {
        onSortColumn(field, order);
      },
      formatter: (cell, row) =>
        middleNameFormatter(
          textFormatter(row?.user?.firstName),
          textFormatter(row?.user?.middleName),
          textFormatter(row?.user?.lastName)
        ),
    },
    {
      dataField: "reportCategoryName",
      text: "Report Category Name",
      // headerClasses: "sorting",
      // headerSortingClasses,
      // sort: true,
      // onSort: (field, order) => {
      //   onSortColumn(field, order);
      // },
      formatter: (cell, row) => textFormatter(row?.reportCategory?.category),
    },

    {
      dataField: "reason",
      text: "Description",
      // headerClasses: "sorting",
      // headerSortingClasses,
      // sort: true,
      // onSort: (field, order) => {
      //   onSortColumn(field, order);
      // },
      formatter: (cell) => readMoreTextShow(cell, showMoreText),
    },
    {
      dataField: "isActive",
      text: "Status",
      // sort: true,
      // headerClasses: "sorting",
      // onSort: (field, order) => {
      //   onSortColumn(field, order);
      // },
      headerSortingClasses,
      formatter: (cell, row) =>
        switchFormatter(
          row?.memorial?.isActive === 1,
          (val) => {
            onChangeStatus(val, row);
          },
          statusLoading,
          row
        ),
    },
    {
      dataField: "action",
      text: "Action",
      headerClasses: "nk-tb-col-tools text-end",
      formatter: (cell, row) => actionFormatter(options(row)),
    },
  ];
  return (
    <>
      <div className="nk-block-head nk-block-head-sm">
        <div className="nk-block-between">
          <PageHeader heading="Reported Biographies">
            <Breadcrumb breadcrumb={breadcrumb} />
          </PageHeader>
          <ListingHeader
            btnArray={["filter"]}
            visible={visible}
            setVisible={setVisible}
            popover={
              <ManageReportedFilter
                onSubmit={handleFilterSubmit}
                loading={loading}
                onReset={onReset}
                filterData={filterData}
                reportCategory={reportCategory}
                reportCategoryLoader={reportCategoryLoader}
                t={t}
              />
            }
          />
        </div>
      </div>
      <DataTable
        hasLimit
        noOfPage={noOfPage}
        sizePerPage={sizePerPage}
        page={page}
        param={param}
        count={totalCount}
        tableData={reportedBiographyList}
        tableColumns={columns}
        setSizePerPage={setSizePerPage}
        getSearchValue={getSearchValue}
        defaultSort={defaultSort}
        tableLoader={loading}
        tableReset={tableReset}
      />

      <ModalComponent
        extraClassName="modal-md"
        show={showReadMore}
        onHandleCancel={onCloseDescriptionModal}
        title="Read More"
      >
        <p className="text-break">{readData}</p>
      </ModalComponent>
    </>
  );
}

export default ReportBiographies;
